import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CheckCircle2, XCircle, FileText, ChevronDown, ChevronRight,
  Send, RotateCcw, Loader2, ArrowRight, Eye,
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { SheetHeader, SheetTitle } from './ui/sheet';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEP_LABELS = {
  step1: 'Step 1 — Nature & Description',
  step2: 'Step 2 — Items & Justification',
  step3: 'Step 3 — Quotations',
  step4: 'Step 4 — Letterhead Request',
  step5: 'Step 5 — Committee Resolution',
};

function VerdictPill({ ok, children, testid }) {
  return (
    <div
      className={`rounded-md border px-3 py-2 flex items-center gap-2 text-sm ${
        ok
          ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
          : 'border-rose-300 bg-rose-50 text-rose-900'
      }`}
      data-testid={testid}
    >
      {ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
      <span>{children}</span>
    </div>
  );
}

function ExpanderSection({ title, defaultOpen = false, children, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border rounded-md" data-testid={testid}>
      <button
        type="button"
        className="w-full px-3 py-2 flex items-center gap-2 text-left text-sm font-medium hover:bg-muted/40"
        onClick={() => setOpen((p) => !p)}
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        {title}
      </button>
      {open && <div className="px-3 pb-3 pt-1 space-y-2">{children}</div>}
    </div>
  );
}

const inr = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const downloadAttachment = (claimId, attId) => {
  window.open(`${API}/admin/grant-claims/${claimId}/attachments/${attId}/download`, '_blank');
};

// Compact "Preview" button shown next to every uploaded document on the
// admin side. Opens the PDF in a new tab so the admin doesn't lose their
// review context. Falls back gracefully when the attachment object is
// missing (legacy claims sometimes carry the id without the metadata).
function PreviewButton({ claimId, attId, label = 'Preview document', testid }) {
  if (!attId) return null;
  return (
    <Button
      size="sm" variant="outline"
      className="h-7 text-xs"
      onClick={() => downloadAttachment(claimId, attId)}
      data-testid={testid}
    >
      <Eye className="h-3 w-3 mr-1" /> {label}
    </Button>
  );
}

// Admin-side, wizard-aware claim detail panel. Rendered inside the existing
// <SheetContent> in GrantClaims.js when claim.flow_type === 'wizard'.
// Sprint 2 (P0): read-only viewer + Approve + plain Send-back (existing API).
// Sprint 3 will introduce a dedicated per-step cascade-reset send-back endpoint.
export default function AdminWizardClaimDetail({ claim, onAction }) {
  const [acting, setActing] = useState(false);
  const [approvedAmount, setApprovedAmount] = useState(
    claim.approved_amount != null ? String(claim.approved_amount) : ''
  );
  const [reviewerNote, setReviewerNote] = useState(claim.approval_note || '');
  const [sendBackOpen, setSendBackOpen] = useState(false);
  const [sendBackNote, setSendBackNote] = useState('');

  const verdicts = claim.wizard_step_verdicts || {};
  const checklist = [
    { key: 'step1', ok: verdicts.step1?.verdict === 'specific' },
    { key: 'step2', ok: verdicts.step2?.verdict === 'pass' },
    { key: 'step3', ok: verdicts.step3?.verdict === 'pass' },
    { key: 'step4', ok: (claim.letterhead_verdict?.verdict || verdicts.step4?.verdict) === 'pass' },
    { key: 'step5', ok: !!claim.resolution_attachment_id },
  ];

  const allPassed = checklist.every((c) => c.ok);
  const items = claim.proposed_items || [];
  const grandTotal = items.reduce((s, r) => s + (Number(r.estimated_cost) || 0), 0);
  const lv = claim.letterhead_verdict || {};
  const attsByDocType = (claim.attachments || []).reduce((acc, a) => {
    acc[a.doc_type] = a;
    return acc;
  }, {});
  const sentBackPending = !!claim.sent_back_pending;
  // Pipeline transitions allowed by the backend.
  // Status === 'paid' is terminal — no actions shown.
  const FORWARD_NEXT = {
    submitted: { status: 'approved', label: 'Approve claim', icon: 'check' },
    in_process: { status: 'approved', label: 'Approve claim', icon: 'check' },
    approved: { status: 'payment_initiated', label: 'Mark payment initiated', icon: 'arrow' },
    payment_initiated: { status: 'paid', label: 'Mark as paid', icon: 'arrow' },
  };
  const nextStage = FORWARD_NEXT[claim.status];
  const isTerminal = claim.status === 'paid';
  // The approved amount is editable on every non-terminal stage so the
  // admin can dial it down (less than asked) at any point before "paid".
  // It's hidden only when terminal.
  const showAmountInput = !isTerminal;
  // For wizard claims `grandTotal` (sum of items) is what the secretary
  // actually asked for. `claim_amount` may still be the policy cap from
  // claim-creation time on older claims, so prefer grandTotal when present.
  const claimedAmount = grandTotal > 0 ? grandTotal : (Number(claim.claim_amount) || 0);
  // The admin can approve up to the policy's eligible cap — never more.
  // If the division submitted over-cap, that excess is non-reimbursable.
  const eligibleCap = Number(claim.policy?.eligible_amount) || 0;
  const approvalCeiling = (eligibleCap > 0 && claimedAmount > 0)
    ? Math.min(claimedAmount, eligibleCap)
    : (eligibleCap || claimedAmount);
  const overCap = eligibleCap > 0 && claimedAmount > eligibleCap;
  const currentApproved = Number(claim.approved_amount) || 0;
  const enteredApproved = Number(approvedAmount) || 0;

  const moveForward = async () => {
    if (!nextStage) return;
    const confirmMsg = nextStage.status === 'approved'
      ? (enteredApproved && enteredApproved < claimedAmount
          ? `Approve this claim for ₹${enteredApproved.toLocaleString('en-IN')} (less than the ₹${claimedAmount.toLocaleString('en-IN')} claimed)?`
          : 'Approve this claim?')
      : `Move this claim to "${nextStage.label}"?`;
    if (!window.confirm(confirmMsg)) return;
    setActing(true);
    try {
      // Always persist the approved amount whenever the admin has entered
      // one and we're transitioning forward (or it differs from the saved
      // value). The backend caps it at `<= claim_amount`, so the
      // partial-approval scenario is fully supported.
      const targetAmount = enteredApproved || (showAmountInput ? grandTotal : currentApproved);
      if (targetAmount > 0 && targetAmount !== currentApproved) {
        await axios.put(
          `${API}/admin/grant-claims/${claim.id}/approved-amount`,
          { approved_amount: targetAmount, note: reviewerNote || null },
          { withCredentials: true },
        );
      }
      await axios.put(
        `${API}/admin/grant-claims/${claim.id}/status`,
        { status: nextStage.status, note: reviewerNote || null },
        { withCredentials: true },
      );
      toast.success(`Claim marked ${nextStage.label.toLowerCase()}`);
      onAction?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally { setActing(false); }
  };

  // Standalone "Save approved amount" — lets the admin adjust the
  // partial-approval number without advancing the claim status. Useful
  // when correcting a typo after the claim is already approved.
  const saveApprovedAmount = async () => {
    if (!enteredApproved || enteredApproved <= 0) {
      toast.error('Enter an approved amount first'); return;
    }
    if (enteredApproved > approvalCeiling) {
      const ceilingLabel = (overCap && approvalCeiling === eligibleCap)
        ? `the policy's eligible cap of ₹${eligibleCap.toLocaleString('en-IN')}`
        : `the claimed ₹${claimedAmount.toLocaleString('en-IN')}`;
      toast.error(`Cannot exceed ${ceilingLabel}`); return;
    }
    setActing(true);
    try {
      await axios.put(
        `${API}/admin/grant-claims/${claim.id}/approved-amount`,
        { approved_amount: enteredApproved, note: reviewerNote || null },
        { withCredentials: true },
      );
      toast.success(`Approved amount set to ₹${enteredApproved.toLocaleString('en-IN')}`);
      onAction?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setActing(false); }
  };

  const sendBack = async () => {
    if (!sendBackNote.trim()) { toast.error('Please add a reason'); return; }
    setActing(true);
    try {
      await axios.put(
        `${API}/admin/grant-claims/${claim.id}/status`,
        { send_back: true, note: sendBackNote.trim() },
        { withCredentials: true },
      );
      toast.success('Claim sent back to the division');
      setSendBackOpen(false);
      setSendBackNote('');
      onAction?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send-back failed');
    } finally { setActing(false); }
  };

  const statusBadgeLabel = sentBackPending
    ? 'SENT BACK · AWAITING REVISION'
    : (claim.status === 'submitted' ? 'AWAITING REVIEW'
      : claim.status === 'approved' ? 'APPROVED'
      : claim.status === 'payment_initiated' ? 'PAYMENT INITIATED'
      : claim.status === 'paid' ? 'PAID'
      : (claim.status || '').toUpperCase());

  return (
    <div data-testid="admin-wizard-claim-detail">
      <SheetHeader className="border-b pb-4 mb-4">
        <SheetTitle className="font-heading flex items-start justify-between gap-3">
          <span>
            {claim.division?.name || 'Claim'}
            <span className="block text-sm font-normal text-muted-foreground mt-1">
              {claim.policy?.title} · {claim.fiscal_year} · Wizard claim
            </span>
          </span>
          <Badge className={`text-sm px-3 py-1 ${
            sentBackPending ? 'bg-amber-100 text-amber-900'
              : allPassed ? 'bg-emerald-100 text-emerald-900'
              : 'bg-sky-100 text-sky-900'
          }`}>
            {statusBadgeLabel}
          </Badge>
        </SheetTitle>
      </SheetHeader>

      {/* 5-step verdict checklist */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4" data-testid="admin-wizard-checklist">
        {checklist.map((c) => (
          <VerdictPill key={c.key} ok={c.ok} testid={`admin-wizard-checklist-${c.key}`}>
            {STEP_LABELS[c.key]}
          </VerdictPill>
        ))}
      </div>

      {/* Top-line summary panel */}
      <div className="rounded-md border bg-card p-3 mb-4 text-sm space-y-1.5">
        <div className="flex justify-between border-b pb-1.5">
          <span className="text-muted-foreground">Items proposed</span>
          <span className="font-medium">{items.length}</span>
        </div>
        <div className="flex justify-between border-b pb-1.5">
          <span className="text-muted-foreground">Grand total (asked)</span>
          <span className="font-medium" data-testid="admin-wizard-grand-total">{inr(grandTotal)}</span>
        </div>
        {currentApproved > 0 && (
          <div className="flex justify-between border-b pb-1.5">
            <span className="text-muted-foreground">Approved by MPL</span>
            <span className={`font-semibold ${
              currentApproved < claimedAmount ? 'text-amber-700' : 'text-emerald-700'
            }`} data-testid="admin-wizard-approved-by-mpl">
              {inr(currentApproved)}
              {currentApproved < claimedAmount && (
                <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 font-medium uppercase">
                  Partial · −{inr(claimedAmount - currentApproved)}
                </span>
              )}
            </span>
          </div>
        )}
        {lv.signatory_name && (
          <div className="flex justify-between border-b pb-1.5">
            <span className="text-muted-foreground">Signed by</span>
            <span className="font-medium">
              {lv.signatory_name}{lv.signatory_designation ? ` · ${lv.signatory_designation}` : ''}
            </span>
          </div>
        )}
        {claim.submitted_at && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Submitted at</span>
            <span className="font-medium">{new Date(claim.submitted_at).toLocaleString()}</span>
          </div>
        )}
      </div>

      {/* Per-step expandable viewers */}
      <div className="space-y-2 mb-4">
        <ExpanderSection title="Step 1 — Nature & Description PDF" defaultOpen testid="admin-step1-section">
          <p className="text-xs text-muted-foreground italic">{verdicts.step1?.remark || '—'}</p>
          {attsByDocType.infrastructure_description && (
            <PreviewButton
              claimId={claim.id}
              attId={attsByDocType.infrastructure_description.id}
              label="Preview description PDF"
              testid="admin-step1-preview"
            />
          )}
        </ExpanderSection>

        <ExpanderSection title={`Step 2 — Items (${items.length})`} testid="admin-step2-section">
          {items.length === 0 ? (
            <p className="text-xs text-muted-foreground">No items</p>
          ) : items.map((r, idx) => {
            const jv = r.justification_verdict || {};
            return (
              <div key={r.id} className={`border rounded p-2 text-xs space-y-1 ${
                jv.verdict === 'pass' ? 'border-emerald-200' : 'border-rose-200'
              }`}>
                <div className="flex justify-between gap-2">
                  <span className="font-medium">#{idx + 1} — {r.item}</span>
                  <span>{inr(r.estimated_cost)} · {r.timeline_weeks || 0} wks</span>
                </div>
                {r.details && <p className="text-muted-foreground">{r.details}</p>}
                <p className="italic">{r.justification}</p>
                {jv.remark && <p className={jv.verdict === 'pass' ? 'text-emerald-700' : 'text-rose-700'}>
                  AI: {jv.remark}
                </p>}
              </div>
            );
          })}
        </ExpanderSection>

        <ExpanderSection title="Step 3 — Quotations" testid="admin-step3-section">
          {items.filter((r) => r.quotation_attachment_id).length === 0 ? (
            <p className="text-xs text-muted-foreground">No quotations uploaded</p>
          ) : items.map((r) => {
            if (!r.quotation_attachment_id) return null;
            const qv = r.quotation_verdict || {};
            const checks = qv.checks || {};
            // Resolve the attachment by ID first (always available), then
            // fall back to doc_type lookup. The previous code-path relied
            // on doc_type alone, which broke after cascade-resets that
            // re-keyed proposed_items.
            const att = (claim.attachments || []).find((a) => a.id === r.quotation_attachment_id)
              || attsByDocType[`quotation_${r.id}`];
            return (
              <div key={r.id} className={`border rounded p-2 text-xs space-y-1 ${
                qv.verdict === 'pass' ? 'border-emerald-200' : 'border-rose-200'
              }`}>
                <div className="flex justify-between gap-2">
                  <span className="font-medium">{r.item}</span>
                  <span>{qv.vendor_name || '?'} · {inr(qv.extracted_amount)}</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {['letterhead', 'signature', 'gstin', 'amount_match'].map((k) => {
                    const v = checks[k];
                    const isGstin = k === 'gstin';
                    const ok = v === 'pass';
                    const naGstin = isGstin && !ok;
                    const cls = ok ? 'bg-emerald-100 text-emerald-800'
                      : naGstin ? 'bg-slate-100 text-slate-700'
                      : 'bg-rose-100 text-rose-800';
                    return (
                      <span key={k} className={`px-1.5 py-0.5 rounded text-[10px] ${cls}`}>
                        {k}: {naGstin ? 'n/a' : (v || 'fail')}
                      </span>
                    );
                  })}
                </div>
                {qv.remark && <p className={qv.verdict === 'pass' ? 'text-emerald-700' : 'text-rose-700'}>
                  {qv.remark}
                </p>}
                <PreviewButton
                  claimId={claim.id}
                  attId={att?.id || r.quotation_attachment_id}
                  label="Preview quotation"
                  testid={`admin-step3-preview-${r.id}`}
                />
              </div>
            );
          })}
        </ExpanderSection>

        <ExpanderSection title="Step 4 — Letterhead Request" testid="admin-step4-section">
          {lv.verdict ? (
            <>
              <div className="flex flex-wrap gap-1 text-xs">
                {['division_letterhead', 'signatory', 'items_match', 'amount_match'].map((k) => (
                  <span key={k} className={`px-1.5 py-0.5 rounded text-[10px] ${
                    lv.checks?.[k] === 'pass' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                  }`}>{k}: {lv.checks?.[k] || 'fail'}</span>
                ))}
              </div>
              <p className="text-xs italic mt-1">{lv.remark}</p>
            </>
          ) : <p className="text-xs text-muted-foreground">No letterhead uploaded</p>}
          {attsByDocType.letterhead_request && (
            <PreviewButton
              claimId={claim.id}
              attId={attsByDocType.letterhead_request.id}
              label="Preview letterhead"
              testid="admin-step4-preview"
            />
          )}
        </ExpanderSection>

        <ExpanderSection title="Step 5 — Committee Resolution" testid="admin-step5-section">
          {claim.resolution_attachment_id ? (
            <>
              <p className="text-xs flex items-center gap-1">
                <FileText className="h-3 w-3" /> {claim.resolution_filename || 'Resolution uploaded'}
              </p>
              <PreviewButton
                claimId={claim.id}
                attId={
                  attsByDocType.committee_resolution?.id
                  || claim.resolution_attachment_id
                }
                label="Preview resolution"
                testid="admin-step5-preview"
              />
            </>
          ) : <p className="text-xs text-muted-foreground">No resolution uploaded</p>}
        </ExpanderSection>
      </div>

      {/* Action bar — visible for every non-terminal status. Shows a primary
          "forward" button (next pipeline step) and a secondary "Send back"
          for all states except paid. While the claim is already sent-back,
          we hide the bar so the admin can't double-process before the
          secretary resubmits. */}
      {!isTerminal && !sentBackPending && (
        <div className="rounded-md border bg-card p-3 space-y-2 mb-3" data-testid="admin-wizard-action-bar">
          {showAmountInput && (
            <div>
              <label className="text-xs font-medium text-muted-foreground">
                Approved amount{' '}
                <span className="text-[10px] text-muted-foreground/70">
                  · max ₹{approvalCeiling.toLocaleString('en-IN')}
                  {overCap ? ' (policy eligible cap)' : ' (claimed)'}
                </span>
              </label>
              <Input
                type="number" min={0} max={approvalCeiling} value={approvedAmount}
                placeholder={String(currentApproved || approvalCeiling)}
                onChange={(e) => setApprovedAmount(e.target.value)}
                data-testid="admin-wizard-approved-amount"
              />
              {overCap && (
                <p className="text-[11px] text-amber-700 mt-1" data-testid="admin-wizard-overcap-hint">
                  Division asked ₹{claimedAmount.toLocaleString('en-IN')}, which is over the
                  policy&apos;s eligible cap of ₹{eligibleCap.toLocaleString('en-IN')}.
                  Approval is capped at the policy ceiling.
                </p>
              )}
              {enteredApproved > 0 && enteredApproved < claimedAmount && (
                <p className="text-[11px] text-amber-700 mt-1" data-testid="admin-wizard-partial-hint">
                  Partial approval — division will be paid ₹{enteredApproved.toLocaleString('en-IN')}
                  {' '}instead of the claimed ₹{claimedAmount.toLocaleString('en-IN')}{' '}
                  (savings ₹{(claimedAmount - enteredApproved).toLocaleString('en-IN')}).
                </p>
              )}
              {enteredApproved > approvalCeiling && (
                <p className="text-[11px] text-rose-700 mt-1">
                  Cannot exceed ₹{approvalCeiling.toLocaleString('en-IN')}
                  {overCap ? ' (policy eligible cap).' : ' (claimed).'}
                </p>
              )}
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-muted-foreground">Reviewer note (optional)</label>
            <Textarea rows={2} value={reviewerNote}
              onChange={(e) => setReviewerNote(e.target.value)}
              data-testid="admin-wizard-reviewer-note"
            />
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            {nextStage && (
              <Button
                onClick={moveForward} disabled={acting || enteredApproved > approvalCeiling}
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid="admin-wizard-forward-btn"
              >
                {acting
                  ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  : nextStage.icon === 'check'
                    ? <CheckCircle2 className="h-3 w-3 mr-1" />
                    : <ArrowRight className="h-3 w-3 mr-1" />}
                {nextStage.label}
              </Button>
            )}
            {/* Save-only path: lets the admin change the approved amount
                on an already-approved claim WITHOUT advancing the status. */}
            {showAmountInput && enteredApproved > 0 && enteredApproved !== currentApproved && (
              <Button
                variant="secondary"
                onClick={saveApprovedAmount}
                disabled={acting || enteredApproved > approvalCeiling}
                data-testid="admin-wizard-save-amount-btn"
              >
                Save approved amount only
              </Button>
            )}
            <Button variant="outline" onClick={() => setSendBackOpen(true)} disabled={acting}
              data-testid="admin-wizard-sendback-btn">
              <RotateCcw className="h-3 w-3 mr-1" /> Send back
            </Button>
          </div>
        </div>
      )}

      {/* Send-back dialog */}
      <Dialog open={sendBackOpen} onOpenChange={setSendBackOpen}>
        <DialogContent data-testid="admin-wizard-sendback-dialog">
          <DialogHeader><DialogTitle>Send claim back for revision</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              The secretary receives an email with this note and can edit the wizard before resubmitting.
            </p>
            <div>
              <label className="text-xs font-medium">Reason (shown to the secretary)</label>
              <Textarea rows={3} value={sendBackNote}
                onChange={(e) => setSendBackNote(e.target.value)}
                placeholder="What needs to be corrected?"
                data-testid="admin-wizard-sendback-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendBackOpen(false)}>Cancel</Button>
            <Button onClick={sendBack} disabled={acting} data-testid="admin-wizard-sendback-confirm">
              <Send className="h-3 w-3 mr-1" /> Send back
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sent-back banner (visible when admin already bounced this claim) */}
      {sentBackPending && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 mb-3"
          data-testid="admin-wizard-sentback-banner">
          <strong>Sent back to the division.</strong> Waiting for them to revise and resubmit.
        </div>
      )}
    </div>
  );
}
